const extensionToMimeType: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  gif: 'image/gif',
  avif: 'image/avif',
}

export const acceptedImageExtensions = Object.keys(extensionToMimeType)
export const acceptedImageTypes = Array.from(new Set(Object.values(extensionToMimeType)))
export const imageInputAccept = '.png,.jpg,.jpeg,.webp,.svg,.gif,.avif'
export const supportedImageTypesLabel = acceptedImageExtensions.join(', ')

const getRawMimeType = (value?: string) => value?.toLowerCase().trim().split(';')[0] || ''

export const normalizeImageType = (value?: string) => {
  if (!value) {
    return 'image/png'
  }

  const normalizedValue = getRawMimeType(value)

  if (normalizedValue in extensionToMimeType) {
    return extensionToMimeType[normalizedValue]
  }

  if (normalizedValue === 'image/jpg') {
    return 'image/jpeg'
  }

  if (acceptedImageTypes.includes(normalizedValue)) {
    return normalizedValue
  }

  return 'image/png'
}

export const getImageFileType = (file: File) => {
  if (file.type) {
    return normalizeImageType(file.type)
  }

  const extension = file.name.split('.').pop()?.toLowerCase()
  return normalizeImageType(extension)
}

export const isSupportedImageFile = (file: File) => {
  const extension = file.name.split('.').pop()?.toLowerCase()
  const rawMimeType = getRawMimeType(file.type)

  if (extension && acceptedImageExtensions.includes(extension)) {
    return true
  }

  if (rawMimeType === 'image/jpg') {
    return true
  }

  return acceptedImageTypes.includes(rawMimeType)
}

export const getPreferredCroppedMimeType = (value?: string) => {
  const normalizedValue = normalizeImageType(value)

  if (normalizedValue === 'image/jpeg') {
    return normalizedValue
  }

  if (normalizedValue === 'image/png') {
    return normalizedValue
  }

  if (normalizedValue === 'image/webp') {
    return normalizedValue
  }

  return 'image/png'
}

const createImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.setAttribute('crossOrigin', 'anonymous')
    image.src = url
  })

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality?: number) =>
  new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality)
  })

export const createAutoSquareImage = async (file: File) => {
  const sourceUrl = URL.createObjectURL(file)

  try {
    const image = await createImage(sourceUrl)
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
      throw new Error('Could not create image canvas')
    }

    const sourceWidth = image.naturalWidth || image.width
    const sourceHeight = image.naturalHeight || image.height
    const outputSize = Math.max(sourceWidth, sourceHeight)
    const offsetX = (outputSize - sourceWidth) / 2
    const offsetY = (outputSize - sourceHeight) / 2
    const mimeType = getPreferredCroppedMimeType(file.type || getImageFileType(file))
    const quality = mimeType === 'image/jpeg' ? 0.92 : undefined

    canvas.width = outputSize
    canvas.height = outputSize

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, outputSize, outputSize)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.drawImage(image, offsetX, offsetY, sourceWidth, sourceHeight)

    let blob = await canvasToBlob(canvas, mimeType, quality)

    if (!blob) {
      blob = await canvasToBlob(canvas, 'image/png')
    }

    if (!blob) {
      throw new Error('Could not create processed image')
    }

    return new File([blob], file.name, { type: blob.type || mimeType })
  } finally {
    URL.revokeObjectURL(sourceUrl)
  }
}
